import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import TopBar from "../top-bar";
import { useReqType } from "@/stores/req-type";
import CandidateAvailabilitySheet from "../dialogs/candidate/availability";
import { Dot, Lock } from "lucide-react";

import { useMyCandidateQuery } from "@/queries/candidate/my-candidate";
import { useCandidateOpportunities } from "@/queries/candidate/opportunities";
import { useConversationsQuery } from "@/queries/chat/user-conversation";

const DashboardLayout = () => {
  const { candidate: candidateObj } = useMyCandidateQuery();
  const { candidateOpportunities } = useCandidateOpportunities();
  const { chatConversations } = useConversationsQuery();

  const { company, candidate, admin } = useReqType((s) => s.reqType);

  const { pathname } = useLocation();

  const hasOpportunityWaiting = useMemo(
    () =>
      candidateOpportunities?.some(
        (opportunity) =>
          opportunity.candidateStatus?.status === "Checking Interest",
      ),
    [candidateOpportunities],
  );

  const hasMessageWaiting = useMemo(
    () =>
      chatConversations?.some(
        (conversation) =>
          conversation.lastMessage &&
          // User hasn't seen any of the messages
          (!conversation.lastSeenAt ||
            // Or they haven't seen the most recent one (at least)
            conversation.lastMessage.createdAt > conversation.lastSeenAt),
      ),
    [chatConversations],
  );

  return (
    <div id="dashboard-wrapper" className="bg-background">
      <TopBar.Main>
        <TopBar.NavItems>
          {candidate && <TopBar.NavItem href="/dashboard" title="Home" />}
          {(company || candidate || admin) && (
            <>
              <TopBar.NavItem
                href="/opportunities"
                title="Opportunities"
                icon={
                  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                  hasOpportunityWaiting || hasMessageWaiting ? (
                    <Dot className="!h-8 !w-8" stroke="red" />
                  ) : undefined
                }
              />
            </>
          )}
          {company && (
            <>
              <TopBar.NavItem
                href="/company/profile/edit"
                title="Company Profile"
              />
              <TopBar.NavItem href="/team" title="Team" />
            </>
          )}
          {candidate && (
            <>
              <TopBar.NavItem
                href="/profile/edit"
                title="My Profile"
                icon={
                  candidateObj && !candidateObj.applicationComplete ? (
                    <Lock />
                  ) : undefined
                }
              />
              <CircleNavItem />
            </>
          )}
          {admin && (
            <>
              <TopBar.NavItem
                href="/new-admin/dashboard"
                title="Admin Dashboard"
                activeOverride={pathname.startsWith("/new-admin")}
              />
              <TopBar.NavItem
                href="/admin"
                title="Old Admin"
                activeOverride={pathname.startsWith("/admin")}
              />
            </>
          )}
        </TopBar.NavItems>
        <TopBar.Profile />
        {(company || candidate || admin) && <TopBar.Sidebar />}
      </TopBar.Main>
      <main className="px-6">
        <Outlet />
      </main>
      {candidate && <CandidateAvailabilitySheet />}
    </div>
  );
};

const CircleNavItem = () => {
  const { candidate } = useMyCandidateQuery();

  if (candidate?.applicationComplete) {
    return (
      <TopBar.NavItem
        href="https://bit.ly/CircleVenturous"
        title="Community"
        externalLink
      />
    );
  }
};

export default DashboardLayout;
